<template>
    <!-- ROOT WRAPPER — needed so toast can be placed outside the login form -->
    <div class="login-page-root">

        <!-- INACTIVITY TOAST — fixed top center, completely outside login form layout -->
        <div v-if="inactivityMessage" class="inactivity-toast px-3 py-2 rounded">
            {{ inactivityMessage }}
            <span class="material-symbols-outlined toast-close" style="color: #320404;"
                @click="inactivityMessage = ''">close</span>
        </div>

        <!-- LOGIN PAGE — full screen background with centered login box -->
        <div class="vmc-login-form position-relative d-flex justify-content-center align-items-center vh-100">
            <div>
                <div
                    class="login-box px-4 py-4 mt-2 position-relative bg-white d-flex flex-column justify-content-center align-items-center">

                    <!-- LOGO AND COMPANY NAME -->
                    <div class="login-container d-flex justify-content-center align-items-center">
                        <img class="logo-image" :src="vmcLogo" alt="">
                        <div class="logo-desc">
                            <p class="mb-0 text-center vmc-text-primary" id="VMCMeaning">VERITAS MARITIME CORPORATION
                            </p>
                            <p class="mb-0 text-center" id="VMCTag">QUALITY SERVICE, QUALITY CREW</p>
                        </div>
                    </div>

                    <!-- LOGIN FORM -->
                    <form @submit.prevent="login">
                        <h2 class="text-dark mt-4">Sign In</h2>

                        <!-- USERNAME INPUT -->
                        <div class="vmc-input-box my-4 position-relative mt-5">
                            <span class="icon position-absolute">
                                <i class="text-dark bi bi-person-fill"></i>
                            </span>
                            <input class="text-dark w-100 bg-transparent border-0" type="text" v-model="username"
                                required>
                            <label class="position-absolute">Username</label>
                        </div>

                        <!-- PASSWORD INPUT with show/hide toggle -->
                        <div class="vmc-input-box my-4 position-relative">
                            <span class="icon position-absolute" @click="showPassword = !showPassword"
                                style="cursor:pointer;">
                                <i v-if="showPassword" class="text-dark bi bi-eye-fill"></i>
                                <i v-else class="text-dark bi bi-eye-slash-fill"></i>
                            </span>
                            <input class="text-dark w-100 bg-transparent border-0"
                                :type="showPassword ? 'text' : 'password'" v-model="password" required>
                            <label class="position-absolute">Password</label>
                        </div>

                        <!-- ERROR MESSAGE — shown on failed login attempt -->
                        <p v-if="errorMessage" class="text-danger text-center" style="font-size:0.85rem;">
                            {{ errorMessage }}
                        </p>

                        <!-- LOGIN BUTTON -->
                        <div class="d-flex justify-content-center align-items-center">
                            <button type="submit"
                                class="text-white font-weight-bold vmc-bg-prime-4 border-0 p-2 text-uppercase"
                                :disabled="loading">
                                {{ loading ? 'Signing in...' : 'Login' }}
                            </button>
                        </div>

                        <!-- KEEP ME SIGNED IN + FORGOT PASSWORD -->
                        <div class="signed-in-forgot d-flex justify-content-between mt-4">
                            <label class="d-flex" style="gap:3px; cursor:pointer;">
                                <input type="checkbox" v-model="rememberMe">
                                <span class="text-dark">Stay signed in</span>
                            </label>
                            <!-- <span class="vmc-span">
                                <router-link :to="{ name: 'SendEmailOTP' }">Forgot Password?</router-link>
                            </span> -->
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "LoginPage",

    // REACTIVE DATA — form fields and UI state
    data() {
        return {
            vmcLogo: require(`@/assets/veritas_logo.png`),
            username: '',
            password: '',
            showPassword: false,
            errorMessage: '',
            loading: false,
            rememberMe: false, // Added tracking for the checkbox

            // INACTIVITY MESSAGE — shown when user was auto-logged out
            inactivityMessage: '',
        }
    },

    mounted() {
        // ALREADY LOGGED IN CHECK — bounce them to dashboard so back button doesn't trap them here
        if (localStorage.getItem('vmc_token')) {
            this.$router.replace('/admin/dashboard');
            return;
        }

        // INACTIVITY CHECK — detect if redirected from CMS due to inactivity
        if (this.$route.query.reason === 'inactivity') {
            this.inactivityMessage = 'Inactivty detected.';

            // FADE OUT then dismiss after 10 seconds
            setTimeout(() => {
                const toast = document.querySelector('.inactivity-toast');
                if (toast) toast.classList.add('fade-out');

                // REMOVE after fade out animation completes (0.5s)
                setTimeout(() => {
                    this.inactivityMessage = '';
                }, 500);
            }, 10000);
        }
    },

    methods: {
        // LOGIN — sends credentials to backend and stores JWT token on success
        async login() {
            // VALIDATION — ensure both fields are filled
            if (!this.username || !this.password) {
                this.errorMessage = "Please enter both username and password.";
                return;
            }

            this.loading = true;
            this.errorMessage = '';

            try {
                this.loading = true; // Ensure loading starts
                this.errorMessage = ""; // Clear old errors

                // 1. HARDCODED API CALL — No more process.env ghosting
                const response = await fetch(`https://veritas.com.ph/vmc/api/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        username: this.username,
                        password: this.password
                    })
                });

                const data = await response.json();

                // 2. HANDLE FAILED LOGIN — show error from server
                if (!response.ok) {
                    this.errorMessage = data.message || "Invalid username or password.";
                    return;
                }

                // 3. SUCCESS — save token and data
                localStorage.setItem('vmc_token', data.token);
                localStorage.setItem('vmc_user', data.username);
                localStorage.setItem('vmc_profile', data.profilePicture);
                localStorage.setItem('vmc_role', data.role);

                // Store the user's preference
                localStorage.setItem('vmc_remember', this.rememberMe);

                // 4. REDIRECT
                this.$router.replace('/admin/dashboard');

            } catch (error) {
                // 5. NETWORK ERROR — This usually triggers if the IP/Port is wrong
                this.errorMessage = "Could not connect to server";
                console.error("Login error:", error);
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>

<style scoped>
/* ROOT WRAPPER — neutral container so toast is not affected by login form styles */
.login-page-root {
    position: relative;
}

/* INACTIVITY TOAST — fixed top right, completely outside login form layout */
.inactivity-toast {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 99999;
    width: 300px;
    font-size: 16px;
    background-color: #ffaeae;
    border: 1px solid #ff0707;
    color: #320404;
    animation: toastFadeIn 0.5s ease forwards;
}

.inactivity-toast.fade-out {
    animation: toastFadeOut 0.5s ease forwards;
}

@keyframes toastFadeIn {
    from {
        opacity: 0;
        transform: translateX(10px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes toastFadeOut {
    from {
        opacity: 1;
        transform: translateX(0);
    }

    to {
        opacity: 0;
        transform: translateX(10px);
    }
}

.toast-close {
    position: absolute;
    top: 10px;
    right: 10px;
    cursor: pointer;
    font-size: 18px;
    font-weight: bold;
    color: #000000;
}

/* BACKGROUND IMAGE — full screen ship photo */
.vmc-login-form {
    background-image: url('~@/assets/vessel_cape_sasanqua.jpg');
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
}

/* BLUE OVERLAY — semi-transparent color over the background */
.vmc-login-form::before {
    content: "";
    position: fixed;
    inset: 0;
    background-color: rgba(8, 98, 165, 0.4);
    z-index: 1;
}

/* ENSURE LOGIN BOX IS ABOVE THE OVERLAY */
.vmc-login-form>div {
    position: relative;
    z-index: 2;
}

/* LOGO DESCRIPTION STYLING */
.login-container .logo-desc {
    font-weight: 700;
}

#VMCTag {
    color: var(--vmc-primary-3);
}

/* LOGIN BOX — white card container */
.login-box {
    margin-top: 10px;
    position: relative;
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    border: 2px solid var(--vmc-dark-2);
    border-radius: 20px;
    box-shadow: 0 0 15px 0;
}

h2 {
    font-size: 24px;
    text-align: center;
}

/* INPUT FIELDS — underline style with floating labels */
.vmc-input-box {
    width: 310px;
    border-bottom: 1px solid #000;
}

.vmc-input-box label {
    top: 50%;
    left: 2px;
    transform: translateY(-50%);
    font-size: 1em;
    pointer-events: none;
    transition: 0.3s;
}

/* FLOATING LABEL — moves up when input is focused or filled */
.vmc-input-box input:focus~label,
.vmc-input-box input:valid~label {
    top: -5px;
    font-size: 12px;
}

.vmc-input-box input {
    outline: none;
    height: 50px;
    font-size: 14px;
}

/* EYE ICON — positioned inside the input on the right */
.vmc-input-box .icon {
    right: 8px;
    font-size: 1.2em;
    line-height: 50px;
}

/* LOGIN BUTTON */
div button {
    width: 250px;
    border-radius: 20px;
    letter-spacing: 1px;
}



/* MOBILE STYLES */
@media (min-width: 330px) {
    .login-box {
        background-size: cover;
        background-attachment: fixed;
    }

    .logo-image {
        width: 60px;
    }

    #VMCMeaning,
    #VMCTag {
        font-size: 14px;
    }
}

/* TABLET AND ABOVE */
@media (min-width: 768px) {
    .logo-image {
        width: 70px;
    }

    #VMCMeaning,
    #VMCTag {
        font-size: 16px;
    }
}
</style>